// US place search via OpenStreetMap Nominatim — free, no API key, CORS-open (verified
// `access-control-allow-origin: *`). Scoped to the US (`countrycodes=us`) because the
// forecast data is NWS (US-only). RN-portable: fetch only, no DOM.
//
// Nominatim usage policy: ≤1 req/s + a valid Referer/UA. On web the browser supplies
// both (UA is forbidden to set on fetch); callers debounce + cache (TanStack Query) to
// stay well within it. RN-PORTABILITY NOTE: the RN port must add an identifying
// `User-Agent: 'TripleAWeather/1.x (contact)'` header here (no browser UA there).

export interface GeoResult {
  lat: number
  lon: number
  /** Clean "City, ST" label. */
  label: string
}

const STATE_ABBR: Record<string, string> = {
  Alabama: 'AL', Alaska: 'AK', Arizona: 'AZ', Arkansas: 'AR', California: 'CA',
  Colorado: 'CO', Connecticut: 'CT', Delaware: 'DE', 'District of Columbia': 'DC',
  Florida: 'FL', Georgia: 'GA', Hawaii: 'HI', Idaho: 'ID', Illinois: 'IL',
  Indiana: 'IN', Iowa: 'IA', Kansas: 'KS', Kentucky: 'KY', Louisiana: 'LA',
  Maine: 'ME', Maryland: 'MD', Massachusetts: 'MA', Michigan: 'MI', Minnesota: 'MN',
  Mississippi: 'MS', Missouri: 'MO', Montana: 'MT', Nebraska: 'NE', Nevada: 'NV',
  'New Hampshire': 'NH', 'New Jersey': 'NJ', 'New Mexico': 'NM', 'New York': 'NY',
  'North Carolina': 'NC', 'North Dakota': 'ND', Ohio: 'OH', Oklahoma: 'OK',
  Oregon: 'OR', Pennsylvania: 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
  'South Dakota': 'SD', Tennessee: 'TN', Texas: 'TX', Utah: 'UT', Vermont: 'VT',
  Virginia: 'VA', Washington: 'WA', 'West Virginia': 'WV', Wisconsin: 'WI',
  Wyoming: 'WY', 'Puerto Rico': 'PR', 'U.S. Virgin Islands': 'VI', Guam: 'GU',
  'American Samoa': 'AS', 'Northern Mariana Islands': 'MP',
}

interface NominatimItem {
  lat: string
  lon: string
  name?: string
  display_name?: string
  address?: {
    city?: string
    town?: string
    village?: string
    hamlet?: string
    municipality?: string
    county?: string
    state?: string
  }
}

function cleanLabel(r: NominatimItem): string {
  const a = r.address ?? {}
  const place = a.city || a.town || a.village || a.hamlet || a.municipality || r.name || a.county || ''
  const st = (a.state && STATE_ABBR[a.state]) || a.state || ''
  const label = [place, st].filter(Boolean).join(', ')
  return label || r.display_name?.split(',').slice(0, 2).join(',').trim() || 'Unknown place'
}

/** Search US places by name. Returns deduped "City, ST" results (≤6). */
export async function searchPlaces(query: string, signal?: AbortSignal): Promise<GeoResult[]> {
  const q = query.trim()
  if (q.length < 2) return []
  const params = new URLSearchParams({
    q,
    countrycodes: 'us',
    format: 'jsonv2',
    addressdetails: '1',
    limit: '8',
  })
  const res = await fetch(`https://nominatim.openstreetmap.org/search?${params}`, {
    headers: { Accept: 'application/json' },
    signal,
  })
  if (!res.ok) throw new Error(`Geocoder ${res.status}`)
  const json = (await res.json()) as NominatimItem[]

  const out: GeoResult[] = []
  const seen = new Set<string>()
  for (const r of json) {
    const lat = Number(r.lat)
    const lon = Number(r.lon)
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue
    const label = cleanLabel(r)
    const key = label.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    out.push({ lat, lon, label })
    if (out.length >= 6) break
  }
  return out
}
